import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { Subscription, catchError, concat, concatMap, of, tap } from 'rxjs';

import {
  AdministratorEventCheckInCRUDService,
  AgendaUserCRUDService,
  CompanyCRUDService,
  CompanyReadOnlySchema,
  EventUserCRUDService,
  GroupCRUDService,
  UserService,
} from '@digitalforest/ngx-oak-api';

import { environment } from '../../environments/environment';
import { AuthService } from '../auth.service';
import { CameraPermissionService } from '../camera-permission.service';
import { LoggingService } from '../logging.service';

import { Exception, NotFoundException } from '@zxing/library';
import { ZXingScannerComponent } from '@zxing/ngx-scanner';

@Component({ template: '' })
export abstract class BaseScanPageComponent implements OnInit, OnDestroy {
  protected readonly environment = environment;

  protected administratorId: number | undefined = undefined;
  protected companyId: number;
  protected company?: CompanyReadOnlySchema;

  protected route = inject(ActivatedRoute);
  protected authService = inject(AuthService);
  protected companyCRUDService = inject(CompanyCRUDService);
  protected administratorEventCheckInCRUDService = inject(AdministratorEventCheckInCRUDService);
  protected logger = inject(LoggingService);
  protected cameraPermissionService = inject(CameraPermissionService);

  private groupCRUDService = inject(GroupCRUDService);
  protected userService = inject(UserService);
  protected eventUserCRUDService = inject(EventUserCRUDService);
  protected agendaUserCRUDService = inject(AgendaUserCRUDService);

  private static readonly CAMERA_LABEL_STORAGE_KEY = 'oak-checkin-camera-label';
  private deviceChangeListener: (() => void) | null = null;
  private subscriptions: Subscription[] = [];

  @ViewChild('scanner') scanner!: ZXingScannerComponent;
  failureCount = 0;
  lastPayload: string | null = null;
  errorMessage: string | null = null;
  groupInformation: { name: string }[] = [];
  operationInProgress = false;
  scannerEnabled: boolean;
  mediaDeviceInfoList: MediaDeviceInfo[] = [];
  mediaDeviceInfoIndex = 0;
  adminCheckInCount: number | undefined = undefined;
  currentCameraName = '';

  constructor() {
    this.companyId = parseInt(this.route.snapshot.paramMap.get('company_id') || '0');
    this.scannerEnabled = true;
  }

  async ngOnInit(): Promise<void> {
    // Check camera permissions early for PWA
    const permissionState = await this.cameraPermissionService.checkCameraPermission();
    this.logger.info('[Scanner] Initial camera permission state', permissionState);

    const testMode = this.route.snapshot.queryParamMap.get('test');
    if (testMode === 'true') {
      this.scannerEnabled = false;
      this.setupTestData();
      return;
    }

    const manageGetCompany$ = this.companyCRUDService.manageGetCompany(this.companyId).pipe(
      tap((value) => {
        this.company = { ...value };
      }),
    );

    this.administratorId = this.authService.decodeToken()?.id;

    const manageGetAdministratorEventCheckInCountByAdministrator$ = this.administratorEventCheckInCRUDService
      .manageGetAdministratorEventCheckInCountByAdministrator(this.companyId, this.administratorId!)
      .pipe(
        tap((value) => {
          this.adminCheckInCount = value;
        }),
        catchError((error) => {
          this.logger.error('[Admin Stats] Failed to get initial check-in count', error);
          this.adminCheckInCount = undefined;
          return of(undefined);
        }),
      );

    this.subscriptions.push(concat(manageGetCompany$, manageGetAdministratorEventCheckInCountByAdministrator$).subscribe());

    // Listen for device changes
    this.setupDeviceChangeListener();
  }

  ngOnDestroy(): void {
    // Clean up all subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.subscriptions = [];

    // Clean up device change listener
    if (this.deviceChangeListener && 'mediaDevices' in navigator) {
      navigator.mediaDevices.removeEventListener('devicechange', this.deviceChangeListener);
      this.deviceChangeListener = null;
    }

    // Safely stop the scanner to prevent "Track has ended" errors
    this.stopScannerSafely();
  }

  /**
   * Safely stops the ZXing scanner, catching any InvalidAccessError that might occur
   * when the media track has already ended (e.g., on iOS Safari).
   * Fixes Sentry issues: OAK-CHECKIN-SITE-A, OAK-CHECKIN-SITE-B, OAK-CHECKIN-SITE-9, OAK-CHECKIN-SITE-J, OAK-CHECKIN-SITE-Q
   */
  private stopScannerSafely(): void {
    try {
      // Check if scanner is initialized
      if (!this.scanner) {
        return;
      }

      // Call the scanner's reset method which properly cleans up the component state
      // This ensures the ZXing component won't try to stop the scanner again in its ngOnDestroy
      const scannerWithReset = this.scanner as unknown as {
        reset?: () => void;
        _reset?: () => void;
      };

      // Try calling reset() which internally calls _reset() and scanStop()
      if (typeof scannerWithReset.reset === 'function') {
        try {
          scannerWithReset.reset();
        } catch (error: unknown) {
          const err = error as Error;
          // Silently ignore expected errors during cleanup
          if (
            err.name === 'InvalidStateError' ||
            err.name === 'InvalidAccessError' ||
            (err.message &&
              (err.message.includes('Track has ended') ||
                err.message.includes('already') ||
                err.message.includes('No scanning is running'))) ||
            err.name === 'DOMException'
          ) {
            // Expected during iOS Safari cleanup - ignore
            return;
          }
          this.logger.warn('[Scanner] Error calling reset', err);
        }
      }
    } catch (error) {
      // Catch any synchronous errors during cleanup
      const err = error as Error;
      this.logger.warn('[Scanner] Error during scanner cleanup', err);
    }
  }

  private setupDeviceChangeListener(): void {
    if (!('mediaDevices' in navigator) || !('addEventListener' in navigator.mediaDevices)) {
      this.logger.warn('[Scanner] Device change events not supported');
      return;
    }

    this.deviceChangeListener = async () => {
      this.logger.info('[Scanner] Device change detected, refreshing camera list');

      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter((device) => device.kind === 'videoinput');

        // Store the current camera label before updating the list
        const currentCameraLabel = this.mediaDeviceInfoList[this.mediaDeviceInfoIndex]?.label;

        // Update the device list
        this.mediaDeviceInfoList = videoDevices;

        // Try to restore the camera by its label
        if (!currentCameraLabel) {
          // No label available, keep current index if valid
          if (this.mediaDeviceInfoIndex >= this.mediaDeviceInfoList.length) {
            this.mediaDeviceInfoIndex = 0;
            this.saveCameraLabel();
          }
          this.updateCurrentCameraName();
          return;
        }

        const newIndex = this.findCameraIndexByLabel(currentCameraLabel);
        if (newIndex !== null) {
          this.mediaDeviceInfoIndex = newIndex;
          this.logger.info('[Scanner] Camera restored by label after device change', currentCameraLabel);
        } else {
          // Camera no longer available, reset to first
          this.mediaDeviceInfoIndex = 0;
          this.saveCameraLabel();
          this.logger.warn('[Scanner] Current camera no longer available, reset to first camera');
        }

        this.updateCurrentCameraName();
      } catch (error) {
        this.logger.error('[Scanner] Error handling device change', error);
      }
    };

    navigator.mediaDevices.addEventListener('devicechange', this.deviceChangeListener);
  }

  updateCheckInCount() {
    this.subscriptions.push(
      this.administratorEventCheckInCRUDService
        .manageGetAdministratorEventCheckInCountByAdministrator(this.companyId, this.administratorId!)
        .subscribe({
          next: (value) => {
            this.adminCheckInCount = value;
          },
          error: (error) => {
            this.logger.error('[Admin Stats] Failed to update check-in count', error);
            this.adminCheckInCount = undefined;
          },
        }),
    );
  }

  onScanSuccess(payload: string): void {
    if (payload === this.lastPayload) {
      return;
    }

    this.lastPayload = payload;
    this.operationInProgress = true;

    if (!this.company || !this.company.subdomain) {
      return;
    }

    this.handleScan(payload);
  }

  onScanError(): void {
    // TODO: Add error handling if needed
  }

  onScanFailure(exception: Exception | undefined): void {
    this.failureCount += 1;

    if (exception instanceof NotFoundException) {
      // This is expected when no QR code is detected in the frame
      // Don't log or report to Sentry as it's normal behavior
      return;
    } else if (exception) {
      this.logger.warn('[Scanner] Scan failed with exception', exception);
    }
  }

  onNewScan(): void {
    this.lastPayload = null;
    this.groupInformation = [];
    this.scannerEnabled = true;
  }

  async onCamerasFound($event: MediaDeviceInfo[]): Promise<void> {
    this.mediaDeviceInfoList = [...$event];

    // Request camera permission if needed (especially for PWA)
    const hasPermission = await this.cameraPermissionService.requestCameraPermission();
    if (!hasPermission) {
      this.logger.error('[Scanner] Camera permission denied');
      return;
    }

    // After permissions are granted, the camera list might have changed
    // Wait a bit for browser to populate device labels
    await new Promise((resolve) => setTimeout(resolve, 100));

    // Try to refresh the device list one more time with permissions
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter((device) => device.kind === 'videoinput');
      if (videoDevices.length > 0) {
        this.mediaDeviceInfoList = videoDevices;
      }
    } catch (error) {
      this.logger.warn('[Scanner] Failed to refresh device list after permissions', error);
    }

    // Now try to load saved camera label from local storage
    const savedLabel = this.loadCameraLabel();
    let cameraSelected = false;

    // Try to find camera by saved label
    if (savedLabel) {
      const savedCameraIndex = this.findCameraIndexByLabel(savedLabel);
      if (savedCameraIndex !== null) {
        this.mediaDeviceInfoIndex = savedCameraIndex;
        cameraSelected = true;
        this.logger.info('[Scanner] Restored camera by label from local storage', savedLabel);
      } else {
        this.logger.warn('[Scanner] Saved camera not found in current device list', savedLabel);
      }
    }

    // If no saved camera found, try to find a back-facing camera
    if (!cameraSelected) {
      const backCamera = this.mediaDeviceInfoList.find((device) => {
        const label = device.label.toLowerCase();
        return (
          label.includes('back') ||
          label.includes('environment') ||
          // @ts-expect-error - facingMode might exist on some implementations
          device.facingMode === 'environment'
        );
      });

      // Use back camera if found, otherwise stick with default (index 0)
      if (backCamera) {
        this.mediaDeviceInfoIndex = this.mediaDeviceInfoList.indexOf(backCamera);
      } else {
        this.mediaDeviceInfoIndex = 0;
      }
    }

    // Save the current camera selection
    this.saveCameraLabel();

    // Update current camera name
    this.updateCurrentCameraName();
  }

  async onCamerasNotFound(): Promise<void> {
    this.logger.warn('[Scanner] No cameras found on this device');

    // Try to request permission which might trigger camera availability
    const hasPermission = await this.cameraPermissionService.requestCameraPermission();
    if (!hasPermission) {
      this.logger.error('[Scanner] Camera permission denied or no camera available');
    }

    // Update camera name to indicate no cameras
    this.currentCameraName = 'No camera available';
  }

  onRotate(): void {
    this.mediaDeviceInfoIndex = (this.mediaDeviceInfoIndex + 1) % this.mediaDeviceInfoList.length;
    this.saveCameraLabel();
    this.updateCurrentCameraName();
  }

  onScanTest(): void {
    this.handleScan('TEST');
  }

  protected updateGroupInformation(groupIdList: number[] | null | undefined) {
    if (groupIdList) {
      this.subscriptions.push(
        of(...groupIdList.sort((a: number, b: number) => a - b))
          .pipe(
            concatMap((groupId) => {
              this.logger.debug('[Group] Fetching group information', { groupId, companyId: this.companyId });
              return this.groupCRUDService.manageGetGroup(groupId, this.companyId);
            }),
            tap((value) => {
              this.groupInformation.push({ name: value.name as string });
            }),
          )
          .subscribe(),
      );
    }
  }

  protected abstract handleScan(payload: string): void;

  protected abstract setupTestData(): void;

  private saveCameraLabel(): void {
    try {
      if (this.mediaDeviceInfoList.length === 0 || this.mediaDeviceInfoIndex >= this.mediaDeviceInfoList.length) {
        return;
      }

      const currentDevice = this.mediaDeviceInfoList[this.mediaDeviceInfoIndex];
      if (!currentDevice.label) {
        return;
      }

      localStorage.setItem(BaseScanPageComponent.CAMERA_LABEL_STORAGE_KEY, currentDevice.label);
      this.logger.info('[Scanner] Saved camera label', currentDevice.label);
    } catch (error) {
      this.logger.warn('[Scanner] Failed to save camera label to local storage', error);
    }
  }

  private loadCameraLabel(): string | null {
    try {
      const savedLabel = localStorage.getItem(BaseScanPageComponent.CAMERA_LABEL_STORAGE_KEY);
      if (savedLabel) {
        this.logger.info('[Scanner] Loaded camera label from storage', savedLabel);
      }
      return savedLabel;
    } catch (error) {
      this.logger.warn('[Scanner] Failed to load camera label from local storage', error);
    }
    return null;
  }

  private findCameraIndexByLabel(label: string): number | null {
    const index = this.mediaDeviceInfoList.findIndex((device) => device.label === label);
    return index >= 0 ? index : null;
  }

  private updateCurrentCameraName(): void {
    if (this.mediaDeviceInfoList.length === 0 || this.mediaDeviceInfoIndex >= this.mediaDeviceInfoList.length) {
      this.currentCameraName = 'No camera selected';
      return;
    }

    const currentDevice = this.mediaDeviceInfoList[this.mediaDeviceInfoIndex];
    this.currentCameraName = currentDevice.label || `Camera ${this.mediaDeviceInfoIndex + 1}`;
  }
}
